import Choice, { ChoiceItem } from '@ingka/choice';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ConditionalRender from '../../../components/ConditionalRender';
import {
  getRegisteredTimeSlotAndState,
  getAllRegisteredTimeSlots,
  getSelectedEvent,
  getSelectedTimeslot,
  useAppContext,
} from '../../../contextProviders/AppContext';
import { TimeSlot } from '../../../types/model';
import { formatEventTimeSpan } from '../../../utils/formatDate';
import getCaption from './getCaption';
import { isPassedTimeSlot } from '../../../contextProviders/AppContext/utils';
import IconPill from '@ingka/pill';
import CalendarCancelIcon from '@ingka/ssr-icon/paths/calendar-cancel';
import {
  CHECKIN,
  REGISTERED,
  SELF_CHECKIN,
  WAITING_LIST,
} from '../../Register/enums';

type PropsType = {
  timeSlots: Array<TimeSlot>;
  onSelect: any;
  timeZone: string;
};

const StyledChoice = styled(Choice)`
  display: grid;
  row-gap: var(--space-100);

  > li {
    margin: 0;
  }

  ${({ theme }) => `${theme.breakpoints.lg} {
    row-gap: var(--space-150);
    column-gap: var(--space-150);
    grid-template-columns: auto auto;
  }`}
`;

const Title = styled.p`
  font-weight: bold;
  margin: 0;
`;

const Selectable = ({ timeSlots, onSelect, timeZone }: PropsType) => {
  const { t } = useTranslation();
  const { useSelector } = useAppContext();
  const [registeredToTimeSlot, registrationState] = useSelector(getRegisteredTimeSlotAndState);
  const allRegisteredTimeslots = useSelector(getAllRegisteredTimeSlots);
  const selectedTimeslot = useSelector(getSelectedTimeslot);
  const selectedEvent = useSelector(getSelectedEvent);
  const allowMultipleRegistrations = selectedEvent?.allowMultipleRegistrations ?? false;
  const hasMultipleRegistrations = !allowMultipleRegistrations && allRegisteredTimeslots.length > 1;
  
  const selectedIndex = timeSlots.findIndex(
    (t) => t.timeslotId === selectedTimeslot?.timeslotId
  );

  return (
    <ConditionalRender predicate={selectedIndex > -1}>
      <Title>{t('event.chooseDate')}</Title>
      <StyledChoice onSelect={onSelect} preselectedIndex={selectedIndex}>
        {timeSlots.map((timeSlot: TimeSlot, i: number) => {
          const isPast = isPassedTimeSlot(timeSlot);
          const hasActiveRegistration = timeSlot.registration &&
            [REGISTERED, WAITING_LIST, CHECKIN, SELF_CHECKIN].includes(timeSlot.registration.state);
          const isDisabled =
            isPast || (!allowMultipleRegistrations && registeredToTimeSlot &&
              registeredToTimeSlot !== timeSlot.timeslotId &&
              !(hasMultipleRegistrations && hasActiveRegistration));

          return (
            <ChoiceItem
              key={i}
              id={timeSlot.timeslotId}
              disabled={isDisabled}
              title={
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {isPast && (
                    <IconPill
                      aria-label={t('event.passed')}
                      x-small={true}
                      ssrIcon={CalendarCancelIcon}
                      style={{
                        width: 'fit-content',
                        marginLeft: '-18px',
                        paddingLeft: '10px',
                        paddingRight: '3px',
                        minHeight: '45px',
                        color: '#595959',
                      }}
                    />
                  )}
                  <div>
                    <div style={{ color: isPast ? '#595959' : undefined }}>
                      {formatEventTimeSpan(
                        timeSlot.utcStartDate,
                        timeSlot.utcEndDate,
                        timeSlot.startDate,
                        timeSlot.endDate,
                        timeZone
                      )}
                    </div>
                    <div style={{ color: isPast ? '#595959' : undefined }}>
                      {getCaption(timeSlot, registeredToTimeSlot, registrationState, t, allowMultipleRegistrations)}
                    </div>
                  </div>
                </div>
              }
            />
          );
        })}
      </StyledChoice>
    </ConditionalRender>
  );
};

export default Selectable;